import { haversineDistance } from './math.js';

/**
 * Sea routing using a waypoint network and A* pathfinding.
 * Ships travel through predefined ocean waypoints to avoid land.
 */

let waypointsData = null;
let waypointGraph = null;

/**
 * Initialize the routing system with waypoint data.
 * Call this once at boot with the loaded JSON.
 */
export function initSeaRoutes(data) {
  waypointsData = data;
  waypointGraph = buildGraph(data);
}

/**
 * Build an adjacency graph from waypoint data.
 */
function buildGraph(data) {
  const graph = new Map();

  // Add all waypoints as nodes
  for (const wp of data.waypoints) {
    graph.set(wp.id, {
      id: wp.id,
      lat: wp.lat,
      lng: wp.lng,
      neighbors: []
    });
  }

  // Add connections (bidirectional)
  for (const [a, b] of (data.connections || data.edges)) {
    const nodeA = graph.get(a);
    const nodeB = graph.get(b);
    if (nodeA && nodeB) {
      const dist = haversineDistance(nodeA, nodeB);
      nodeA.neighbors.push({ id: b, distance: dist });
      nodeB.neighbors.push({ id: a, distance: dist });
    }
  }

  return graph;
}

/**
 * Find the nearest waypoint to a given position. Waypoints with no
 * connections are skipped — a stray unconnected point (e.g. left by the
 * waypoint editor) sitting on a port would otherwise make every route to
 * that port fall back to a straight line.
 */
function findNearestWaypoint(pos) {
  if (!waypointsData) return null;

  let nearest = null;
  let minDist = Infinity;

  for (const wp of waypointsData.waypoints) {
    if (!waypointGraph?.get(wp.id)?.neighbors.length) continue;
    const dist = haversineDistance(pos, wp);
    if (dist < minDist) {
      minDist = dist;
      nearest = wp;
    }
  }

  return nearest;
}

/**
 * A* pathfinding through the waypoint network.
 */
function astarPath(startId, endId) {
  if (!waypointGraph) return null;

  const startNode = waypointGraph.get(startId);
  const endNode = waypointGraph.get(endId);
  if (!startNode || !endNode) return null;

  // Priority queue (simple array, sorted by f-score)
  const openSet = [{ id: startId, g: 0, f: haversineDistance(startNode, endNode) }];
  const cameFrom = new Map();
  const gScore = new Map();
  gScore.set(startId, 0);

  while (openSet.length > 0) {
    // Get node with lowest f-score
    openSet.sort((a, b) => a.f - b.f);
    const current = openSet.shift();

    if (current.id === endId) {
      // Reconstruct path
      const path = [endId];
      let node = endId;
      while (cameFrom.has(node)) {
        node = cameFrom.get(node);
        path.unshift(node);
      }
      return path;
    }

    const currentNode = waypointGraph.get(current.id);
    for (const neighbor of currentNode.neighbors) {
      const tentativeG = gScore.get(current.id) + neighbor.distance;

      if (!gScore.has(neighbor.id) || tentativeG < gScore.get(neighbor.id)) {
        cameFrom.set(neighbor.id, current.id);
        gScore.set(neighbor.id, tentativeG);

        const neighborNode = waypointGraph.get(neighbor.id);
        const h = haversineDistance(neighborNode, endNode);
        const f = tentativeG + h;

        // Add to open set if not already there
        if (!openSet.find(n => n.id === neighbor.id)) {
          openSet.push({ id: neighbor.id, g: tentativeG, f });
        }
      }
    }
  }

  return null; // No path found
}

/**
 * Calculate a sea route between two positions.
 * Returns { waypoints: [{lat, lng}, ...], distance: number }
 */
export function calculateRoute(originPos, destPos) {
  // If routing data not loaded, fall back to direct path
  if (!waypointsData || !waypointGraph) {
    return {
      waypoints: [
        { lat: originPos.lat, lng: originPos.lng },
        { lat: destPos.lat, lng: destPos.lng }
      ],
      distance: haversineDistance(originPos, destPos)
    };
  }

  const startWp = findNearestWaypoint(originPos);
  const endWp = findNearestWaypoint(destPos);

  // If same waypoint or very close, just go direct
  if (!startWp || !endWp || startWp.id === endWp.id) {
    return {
      waypoints: [
        { lat: originPos.lat, lng: originPos.lng },
        { lat: destPos.lat, lng: destPos.lng }
      ],
      distance: haversineDistance(originPos, destPos)
    };
  }

  // Find path through waypoint network
  const pathIds = astarPath(startWp.id, endWp.id);

  if (!pathIds) {
    // No path found, fall back to direct
    return {
      waypoints: [
        { lat: originPos.lat, lng: originPos.lng },
        { lat: destPos.lat, lng: destPos.lng }
      ],
      distance: haversineDistance(originPos, destPos)
    };
  }

  // Build full waypoint list: origin -> waypoints -> destination
  const waypoints = [{ lat: originPos.lat, lng: originPos.lng }];

  for (const id of pathIds) {
    const node = waypointGraph.get(id);
    if (node) {
      waypoints.push({ lat: node.lat, lng: node.lng });
    }
  }

  waypoints.push({ lat: destPos.lat, lng: destPos.lng });

  // Calculate total distance along path
  let totalDistance = 0;
  for (let i = 0; i < waypoints.length - 1; i++) {
    totalDistance += haversineDistance(waypoints[i], waypoints[i + 1]);
  }

  return { waypoints, distance: totalDistance };
}

/**
 * Estimate travel days given distance (nautical miles) and speed (knots).
 */
export function estimateTravelDays(distanceNm, speedKnots) {
  if (speedKnots <= 0) return Infinity;
  return distanceNm / (speedKnots * 24);
}
